import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  VStack,
  Text,
  Box,
  SimpleGrid,
  Flex,
  useColorModeValue,
  HStack,
  Icon,
  Badge,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import {
  Check,
  ClipboardList,
  Type,
  Calendar,
  List,
  Paperclip,
  Hash,
  Lightbulb,
  Smartphone,
  Plus,
  Clock,
  Bell,
} from "lucide-react";
import type {
  MissionDefinition,
  CreateMissionPayload,
  UpdateMissionPayload,
  FieldType,
} from "../types";
import { useFormBuilder, type FieldChangeset, type FormBuilderField } from "./useFormBuilder";
import { FormBuilder } from "./FormBuilder";

const PhonePreview = ({
  title,
  description,
  fields,
}: {
  title: string;
  description: string;
  fields: FormBuilderField[];
}) => (
  <Box position="relative" w="176px" mx="auto">
    <Box
      bg="#1a1a1a"
      borderRadius="28px"
      p="7px"
      boxShadow="0 16px 48px rgba(0,0,0,0.28), inset 0 0 0 1px rgba(255,255,255,0.08)"
    >
      <Box bg="white" borderRadius="22px" overflow="hidden" h="340px" position="relative">
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="22px"
          bg="white"
          zIndex={2}
          display="flex"
          alignItems="flex-start"
          justifyContent="center"
          pt="5px"
        >
          <Box w="52px" h="12px" bg="#1a1a1a" borderRadius="full" />
        </Box>

        <Box pt="22px" h="100%">
          {/* Blue progress bar */}
          <Box h="3px" bgGradient="linear(to-r, #009efb, #67c1ff)" />

          <Box p="10px" overflowY="auto" h="calc(100% - 3px)">
            {/* Mission header */}
            <Flex align="center" gap={1.5} mb={1.5}>
              <Flex
                w="28px"
                h="28px"
                borderRadius="8px"
                bg="rgba(0,158,251,0.12)"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon as={ClipboardList} boxSize={3} color="#007bd4" />
              </Flex>
              <Text fontSize="11px" fontWeight={700} color="#0a0a0a" noOfLines={2} lineHeight={1.3}>
                {title || "Sem título"}
              </Text>
            </Flex>

            {description && (
              <Text fontSize="9px" color="#6b7480" mb={2} noOfLines={2} lineHeight={1.4}>
                {description}
              </Text>
            )}

            {/* Fields preview */}
            {fields.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Icon as={Smartphone} boxSize={5} color="#d1d5db" display="block" mx="auto" mb={1} />
                <Text fontSize="9px" color="#9ca3af" lineHeight={1.4}>
                  Adicione campos para ver o preview do que o candidato vai preencher.
                </Text>
              </Box>
            ) : (
              <VStack spacing={1.5} align="stretch" mb={2}>
                {fields.slice(0, 4).map((f, i) => (
                  <Box key={i}>
                    <Text fontSize="9px" fontWeight={600} color="#374151" mb={0.5} noOfLines={1}>
                      {f.label || "Campo sem nome"}
                    </Text>
                    <Box h="20px" borderRadius="5px" borderWidth="1px" borderColor="#e5e7eb" bg="white" />
                  </Box>
                ))}
                {fields.length > 4 && (
                  <Text fontSize="8px" color="#9ca3af">
                    +{fields.length - 4} outros campos...
                  </Text>
                )}
              </VStack>
            )}

            {/* CTA */}
            <Box
              h="28px"
              borderRadius="8px"
              bg="#009efb"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mt={2}
            >
              <Text fontSize="10px" fontWeight={600} color="white">
                Continuar
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

const QUICK_ADD_TYPES: { type: FieldType; icon: React.ElementType; label: string }[] = [
  { type: "string", icon: Type, label: "Texto" },
  { type: "date", icon: Calendar, label: "Data" },
  { type: "select", icon: List, label: "Seleção" },
  { type: "file", icon: Paperclip, label: "Arquivo" },
  { type: "number", icon: Hash, label: "Número" },
];

interface MissionForm {
  title: string;
  description: string;
  order: number;
  is_required: boolean;
  tech_only: boolean;
  is_pj: boolean;
  is_clt: boolean;
  is_active: boolean;
  requires_internal_wait: boolean;
  alert_on_completion: boolean;
}

const DEFAULT_MISSION: MissionForm = {
  title: "",
  description: "",
  order: 1,
  is_required: false,
  tech_only: false,
  is_pj: true,
  is_clt: true,
  is_active: true,
  requires_internal_wait: false,
  alert_on_completion: false,
};
export interface MissionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    missionPayload: CreateMissionPayload | UpdateMissionPayload,
    fieldChanges: FieldChangeset
  ) => Promise<void>;
  mission?: MissionDefinition | null;
  totalMissions?: number;
}

export const MissionFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  mission,
  totalMissions = 0,
}: MissionFormModalProps) => {
  const isEdit = !!mission;

  const [form, setForm] = useState<MissionForm>(DEFAULT_MISSION);
  const [submitting, setSubmitting] = useState(false);
  const [titleError, setTitleError] = useState("");
  const [pendingQuickAdd, setPendingQuickAdd] = useState<FieldType | null>(null);

  const { fields, reorderFields, addField, updateField, deleteField, getChangeset, setHasChanges } =
    useFormBuilder(mission?.fields ?? []);

  useEffect(() => {
    if (isOpen) {
      setForm(
        mission
          ? {
              title: mission.title,
              description: mission.description,
              order: mission.order,
              is_required: mission.is_required,
              tech_only: mission.tech_only,
              is_pj: mission.is_pj,
              is_clt: mission.is_clt,
              is_active: mission.is_active,
              requires_internal_wait: mission.requires_internal_wait,
              alert_on_completion: mission.alert_on_completion,
            }
          : DEFAULT_MISSION
      );
      setTitleError("");
      setHasChanges(false);
    }
  }, [isOpen, mission]);

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setTitleError("Título é obrigatório");
      return;
    }
    if (!form.is_pj && !form.is_clt) {
      setTitleError("Selecione ao menos um regime (PJ ou CLT)");
      return;
    }
    setTitleError("");
    setSubmitting(true);
    try {
      const changeset = getChangeset();
      if (isEdit) {
        await onSubmit(
          {
            title: form.title,
            description: form.description,
            order: form.order,
            is_required: form.is_required,
            tech_only: form.tech_only,
            is_pj: form.is_pj,
            is_clt: form.is_clt,
            is_active: form.is_active,
            requires_internal_wait: form.requires_internal_wait,
            alert_on_completion: form.alert_on_completion,
          } as UpdateMissionPayload,
          changeset
        );
      } else {
        await onSubmit(
          {
            slug: form.title,
            title: form.title,
            description: form.description,
            order: form.order,
            is_required: form.is_required,
            tech_only: form.tech_only,
            is_pj: form.is_pj,
            is_clt: form.is_clt,
            requires_internal_wait: form.requires_internal_wait,
            alert_on_completion: form.alert_on_completion,
          } as CreateMissionPayload,
          changeset
        );
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };
  const pageBg = useColorModeValue("#f5f5f5", "#0a1628");
  const cardBg = useColorModeValue("white", "#0f2040");
  const cardBorder = useColorModeValue("gray.100", "whiteAlpha.80");
  const navBg = useColorModeValue("white", "#0d1b35");
  const navBorder = useColorModeValue("gray.100", "whiteAlpha.80");
  const labelColor = useColorModeValue("gray.400", "gray.500");
  const iconBg = useColorModeValue("blue.50", "rgba(0,158,251,0.12)");
  const iconColor = useColorModeValue("blue.500", "blue.300");
  const panelBg = useColorModeValue("#f0f2f5", "#080f1f");
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.80");
  const titleColor = useColorModeValue("gray.800", "white");
  const titlePlaceholderColor = useColorModeValue("gray.300", "gray.600");
  const descPlaceholderColor = useColorModeValue("gray.300", "gray.600");
  const inputBg = useColorModeValue("gray.50", "#0a1628");
  const emptyDashedBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const emptyAreaBg = useColorModeValue("gray.50", "rgba(255,255,255,0.02)");
  const emptyIconBg = useColorModeValue("gray.100", "rgba(255,255,255,0.04)");
  const emptyIconColor = useColorModeValue("gray.400", "gray.600");
  const breadcrumbSep = useColorModeValue("gray.400", "gray.600");
  const breadcrumbTitle = useColorModeValue("gray.700", "gray.200");
  const tipsBg = useColorModeValue("#fffbeb", "rgba(245,158,11,0.06)");
  const tipsBorder = useColorModeValue("orange.100", "rgba(245,158,11,0.15)");
  const tipsLabel = useColorModeValue("orange.700", "orange.300");
  const tipsText = useColorModeValue("orange.700", "orange.400");
  const waitCardActiveBg = useColorModeValue("orange.50", "rgba(245,158,11,0.06)");
  const waitCardActiveBorder = useColorModeValue("orange.200", "rgba(245,158,11,0.25)");
  const waitIconActiveBg = useColorModeValue("orange.100", "rgba(245,158,11,0.15)");
  const waitIconInactiveBg = useColorModeValue("gray.100", "rgba(255,255,255,0.04)");
  const waitIconActiveColor = useColorModeValue("orange.500", "orange.300");
  const waitIconInactiveColor = useColorModeValue("gray.400", "gray.600");
  const alertCardActiveBg = useColorModeValue("blue.50", "rgba(0,158,251,0.06)");
  const alertCardActiveBorder = useColorModeValue("blue.200", "rgba(0,158,251,0.25)");
  const alertIconActiveBg = useColorModeValue("blue.100", "rgba(0,158,251,0.15)");
  const alertIconActiveColor = useColorModeValue("blue.500", "blue.300");
  const alertLabelActiveColor = useColorModeValue("blue.700", "blue.300");
  const waitLabelActiveColor = useColorModeValue("orange.700", "orange.300");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent
        bg={pageBg}
        m={0}
        borderRadius={0}
        h="100vh"
        display="flex"
        flexDirection="column"
      >
        <Box
          flexShrink={0}
          bg={navBg}
          borderBottomWidth="1px"
          borderColor={navBorder}
          px={{ base: 3, md: 6 }}
          py={3}
          zIndex={10}
        >
          <Flex align="center" justify="space-between" gap={2}>
            <HStack spacing={1.5} fontSize="sm" minW={0} flex={1}>
              <Text
                fontSize="sm"
                fontWeight={500}
                color={labelColor}
                cursor="pointer"
                flexShrink={0}
                _hover={{ color: breadcrumbTitle }}
                onClick={onClose}
              >
                Missões
              </Text>
              <Text color={breadcrumbSep} fontSize="sm" flexShrink={0}>
                /
              </Text>
              <Text fontSize="sm" fontWeight={500} color={breadcrumbTitle} noOfLines={1}>
                {form.title || (isEdit ? "Editar missão" : "Nova missão")}
              </Text>
            </HStack>

            <HStack spacing={2}>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                isDisabled={submitting}
                color={labelColor}
              >
                Descartar
              </Button>
              <Button
                colorScheme="blue"
                size="sm"
                leftIcon={<Check size={14} />}
                onClick={handleSubmit}
                isLoading={submitting}
              >
                {isEdit ? "Salvar missão" : "Criar missão"}
              </Button>
            </HStack>
          </Flex>
        </Box>
        <ModalBody p={0} flex={1} overflow="hidden">
          <Flex h="100%" overflow="hidden">
            <Box flex={1} overflowY="auto" overflowX="hidden" px={{ base: 4, md: 8, lg: 10 }} py={8}>
              <Box maxW="680px" mx="auto">
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={cardBorder}
                  p={{ base: 4, md: 6 }}
                  mb={4}
                >
                  <Flex gap={{ base: 3, md: 5 }} align="flex-start">
                    <Flex
                      w={{ base: "44px", md: "64px" }}
                      h={{ base: "44px", md: "64px" }}
                      borderRadius="xl"
                      bg={iconBg}
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon as={ClipboardList} boxSize={6} color={iconColor} />
                    </Flex>

                    <Box flex={1} minW={0}>
                      <Flex mb={2} gap={1.5} wrap="wrap" align="center">
                        <Text
                          fontSize="11px"
                          fontWeight={700}
                          color={labelColor}
                          textTransform="uppercase"
                          letterSpacing="wider"
                          flexShrink={0}
                        >
                          {isEdit ? "EDITAR MISSÃO" : "NOVA MISSÃO"}
                        </Text>
                        <Badge colorScheme="blue" variant="subtle" fontSize="10px" flexShrink={0}>
                          ONBOARDING
                        </Badge>
                        <Text fontSize="xs" color="blue.500" fontWeight={600} flexShrink={0}>
                          ● ETAPA {form.order}
                        </Text>
                      </Flex>
                      <FormControl isInvalid={!!titleError} mb={1}>
                        <Input
                          variant="unstyled"
                          fontSize={{ base: "lg", md: "2xl" }}
                          fontWeight={700}
                          placeholder="Dê um nome a esta missão..."
                          value={form.title}
                          onChange={(e) => {
                            setForm({ ...form, title: e.target.value });
                            if (e.target.value.trim()) setTitleError("");
                          }}
                          color={titleColor}
                          _placeholder={{ color: titlePlaceholderColor }}
                          letterSpacing="-0.01em"
                        />
                        {titleError && (
                          <FormErrorMessage fontSize="xs">{titleError}</FormErrorMessage>
                        )}
                      </FormControl>
                      <Input
                        variant="unstyled"
                        fontSize="sm"
                        w="100%"
                        color="gray.500"
                        placeholder="Descreva para o candidato o que será preenchido nesta etapa..."
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        _placeholder={{ color: descPlaceholderColor }}
                      />
                    </Box>
                  </Flex>
                </Box>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} mb={4}>
                  <Box
                    bg={cardBg}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={cardBorder}
                    p={4}
                  >
                    <Text
                      fontSize="10px"
                      fontWeight={700}
                      color={labelColor}
                      textTransform="uppercase"
                      letterSpacing="wider"
                      mb={3}
                    >
                      POSIÇÃO
                    </Text>
                    <Flex align="center" gap={2}>
                      <Text fontSize="sm" color="gray.500">
                        Etapa
                      </Text>
                      <NumberInput
                        min={1}
                        value={form.order}
                        onChange={(_, val) => setForm({ ...form, order: isNaN(val) ? 1 : val })}
                        size="sm"
                        w="64px"
                      >
                        <NumberInputField
                          textAlign="center"
                          fontWeight={700}
                          fontSize="md"
                          bg={inputBg}
                          borderRadius="lg"
                          px={2}
                        />
                      </NumberInput>
                      {totalMissions > 0 && (
                        <Text fontSize="sm" color="gray.500">
                          de {isEdit ? totalMissions : totalMissions + 1}
                        </Text>
                      )}
                    </Flex>
                  </Box>
                  <Box
                    bg={cardBg}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={cardBorder}
                    p={4}
                  >
                    <VStack align="stretch" spacing={2.5}>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" fontWeight={500}>
                          Obrigatória
                        </Text>
                        <Switch
                          size="sm"
                          colorScheme="green"
                          isChecked={form.is_required}
                          onChange={(e) => setForm({ ...form, is_required: e.target.checked })}
                        />
                      </Flex>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" fontWeight={500}>
                          Ativa no fluxo
                        </Text>
                        <Switch
                          size="sm"
                          colorScheme="green"
                          isChecked={form.is_active}
                          onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        />
                      </Flex>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" fontWeight={500}>
                          Somente tech
                        </Text>
                        <Switch
                          size="sm"
                          colorScheme="purple"
                          isChecked={form.tech_only}
                          onChange={(e) => setForm({ ...form, tech_only: e.target.checked })}
                        />
                      </Flex>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" fontWeight={500}>
                          Visível para PJ
                        </Text>
                        <Switch
                          size="sm"
                          colorScheme="yellow"
                          isChecked={form.is_pj}
                          onChange={(e) => setForm({ ...form, is_pj: e.target.checked })}
                        />
                      </Flex>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" fontWeight={500}>
                          Visível para CLT
                        </Text>
                        <Switch
                          size="sm"
                          colorScheme="teal"
                          isChecked={form.is_clt}
                          onChange={(e) => setForm({ ...form, is_clt: e.target.checked })}
                        />
                      </Flex>
                    </VStack>
                  </Box>
                </SimpleGrid>
                <Box
                  bg={form.requires_internal_wait ? waitCardActiveBg : cardBg}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor={form.requires_internal_wait ? waitCardActiveBorder : cardBorder}
                  p={4}
                  mb={4}
                  transition="all 0.2s"
                >
                  <Flex align="center" justify="space-between">
                    <HStack spacing={3}>
                      <Flex
                        w="36px"
                        h="36px"
                        borderRadius="lg"
                        bg={form.requires_internal_wait ? waitIconActiveBg : waitIconInactiveBg}
                        align="center"
                        justify="center"
                        flexShrink={0}
                        transition="all 0.2s"
                      >
                        <Icon
                          as={Clock}
                          boxSize={4}
                          color={form.requires_internal_wait ? waitIconActiveColor : waitIconInactiveColor}
                          transition="all 0.2s"
                        />
                      </Flex>
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight={600}
                          color={form.requires_internal_wait ? waitLabelActiveColor : undefined}
                        >
                          Requer aguardo interno
                        </Text>
                        <Text fontSize="xs" color="gray.500" lineHeight={1.4}>
                          O colaborador ficará em espera até que o RH libere esta etapa manualmente.
                        </Text>
                      </Box>
                    </HStack>
                    <Switch
                      size="md"
                      colorScheme="orange"
                      isChecked={form.requires_internal_wait}
                      onChange={(e) => setForm({ ...form, requires_internal_wait: e.target.checked })}
                      flexShrink={0}
                    />
                  </Flex>
                </Box>
                <Box
                  bg={form.alert_on_completion ? alertCardActiveBg : cardBg}
                  borderRadius="xl"
                  borderWidth="1px"
                  borderColor={form.alert_on_completion ? alertCardActiveBorder : cardBorder}
                  p={4}
                  mb={4}
                  transition="all 0.2s"
                >
                  <Flex align="center" justify="space-between">
                    <HStack spacing={3}>
                      <Flex
                        w="36px"
                        h="36px"
                        borderRadius="lg"
                        bg={form.alert_on_completion ? alertIconActiveBg : waitIconInactiveBg}
                        align="center"
                        justify="center"
                        flexShrink={0}
                        transition="all 0.2s"
                      >
                        <Icon
                          as={Bell}
                          boxSize={4}
                          color={form.alert_on_completion ? alertIconActiveColor : waitIconInactiveColor}
                          transition="all 0.2s"
                        />
                      </Flex>
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight={600}
                          color={form.alert_on_completion ? alertLabelActiveColor : undefined}
                        >
                          Alerta ao concluir
                        </Text>
                        <Text fontSize="xs" color="gray.500" lineHeight={1.4}>
                          Envia uma notificação no Google Chat quando o candidato conclui esta etapa.
                        </Text>
                      </Box>
                    </HStack>
                    <Switch
                      size="md"
                      colorScheme="blue"
                      isChecked={form.alert_on_completion}
                      onChange={(e) => setForm({ ...form, alert_on_completion: e.target.checked })}
                      flexShrink={0}
                    />
                  </Flex>
                </Box>
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={cardBorder}
                  overflow="hidden"
                >
                  <Flex align="flex-start" justify="space-between" gap={3} px={{ base: 3, md: 5 }} pt={5} pb={3} wrap="wrap">
                    <Box>
                      <HStack spacing={2} mb={0.5}>
                        <Text fontWeight={700} fontSize="md">
                          Campos do formulário
                        </Text>
                        <Badge
                          colorScheme="blue"
                          variant="subtle"
                          borderRadius="full"
                          px={2}
                          fontSize="xs"
                        >
                          {fields.length}
                        </Badge>
                      </HStack>
                      <Text fontSize="xs" color="gray.400">
                        Defina os dados que o candidato vai preencher nesta etapa. Arraste para reordenar.
                      </Text>
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      leftIcon={<Plus size={14} />}
                      borderRadius="lg"
                      flexShrink={0}
                      onClick={() => setPendingQuickAdd("string")}
                    >
                      Novo campo
                    </Button>
                  </Flex>
                  <Box px={5} pb={fields.length > 0 ? 3 : 0}>
                    {fields.length === 0 ? (
                      <>
                        <Flex
                          direction="column"
                          align="center"
                          py={10}
                          borderRadius="xl"
                          borderWidth="1px"
                          borderStyle="dashed"
                          borderColor={emptyDashedBorder}
                          bg={emptyAreaBg}
                        >
                          <Flex
                            w="44px"
                            h="44px"
                            borderRadius="xl"
                            bg={emptyIconBg}
                            align="center"
                            justify="center"
                            mb={3}
                          >
                            <Icon as={ClipboardList} boxSize={5} color={emptyIconColor} />
                          </Flex>
                          <Text
                            fontSize="sm"
                            fontWeight={600}
                            color={useColorModeValue("gray.500", "gray.400")}
                            mb={1}
                          >
                            Nenhum campo ainda
                          </Text>
                          <Text fontSize="xs" color="gray.400" textAlign="center" maxW="280px">
                            Adicione campos de texto, datas, anexos ou seleções.
                            Você pode reordená-los a qualquer momento.
                          </Text>
                        </Flex>
                        <Box display="none">
                          <FormBuilder
                            fields={fields}
                            onReorder={reorderFields}
                            onAdd={addField}
                            onUpdate={updateField}
                            onDelete={deleteField}
                            pendingQuickAdd={pendingQuickAdd}
                            onClearPendingQuickAdd={() => setPendingQuickAdd(null)}
                            hideAddButton
                          />
                        </Box>
                      </>
                    ) : (
                      <FormBuilder
                        fields={fields}
                        onReorder={reorderFields}
                        onAdd={addField}
                        onUpdate={updateField}
                        onDelete={deleteField}
                        pendingQuickAdd={pendingQuickAdd}
                        onClearPendingQuickAdd={() => setPendingQuickAdd(null)}
                        hideAddButton
                      />
                    )}
                  </Box>
                </Box>
                <Box h={8} />
              </Box>
            </Box>
            <Box
              display={{ base: "none", xl: "flex" }}
              flexDirection="column"
              w="300px"
              overflowY="auto"
              p={5}
              borderLeftWidth="1px"
              borderColor={panelBorder}
              bg={panelBg}
              flexShrink={0}
            >
              <Flex align="center" justify="space-between" mb={4}>
                <HStack spacing={1.5}>
                  <Icon
                    as={Smartphone}
                    boxSize={4}
                    color={useColorModeValue("gray.500", "gray.400")}
                  />
                  <Text fontSize="sm" fontWeight={600}>
                    Pré-visualização
                  </Text>
                </HStack>
                <Text
                  fontSize="10px"
                  fontWeight={700}
                  color={labelColor}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  CANDIDATO
                </Text>
              </Flex>
              <Box
                bg={cardBg}
                borderRadius="2xl"
                p={4}
                mb={4}
                borderWidth="1px"
                borderColor={cardBorder}
              >
                <PhonePreview
                  title={form.title}
                  description={form.description}
                  fields={fields}
                />
              </Box>
              <Box
                bg={tipsBg}
                borderRadius="xl"
                p={4}
                borderWidth="1px"
                borderColor={tipsBorder}
              >
                <HStack spacing={2} mb={2.5}>
                  <Icon as={Lightbulb} boxSize={4} color={useColorModeValue("orange.400", "orange.300")} />
                  <Text fontSize="sm" fontWeight={600} color={tipsLabel}>
                    Dicas rápidas
                  </Text>
                </HStack>
                <VStack align="start" spacing={1.5}>
                  <Text fontSize="xs" color={tipsText} lineHeight={1.5}>
                    • Use nomes curtos e em linguagem natural para os campos
                  </Text>
                  <Text fontSize="xs" color={tipsText} lineHeight={1.5}>
                    • Marque como{" "}
                    <Text as="span" fontWeight={700}>
                      obrigatório
                    </Text>{" "}
                    apenas o que realmente bloqueia a próxima etapa
                  </Text>
                  <Text fontSize="xs" color={tipsText} lineHeight={1.5}>
                    • Use o campo{" "}
                    <Text as="span" fontWeight={700}>
                      detalhes
                    </Text>{" "}
                    para explicar formatos esperados (ex.: PDF colorido)
                  </Text>
                </VStack>
              </Box>
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
